import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { http_get, http_post } from '../../pages/utils/api_client';
import { API_ENDPOINTS } from '../../pages/utils/endpoints';

// BL-082 — CSV audience importer (Option A). The file is parsed in the browser
// so we can offer a column-mapping step + preview; the mapped rows are then
// POSTed as JSON to /audience_imports. Import always creates/updates audience
// members and their attributes (fill-blank). Optionally, the same people can be
// granted access to one material in the same pass (off by default).

const IGNORE = '';
const SEGMENT_VALUES = ['customer', 'trial', 'lead'];

// Minimal RFC-4180-ish CSV parser: handles quoted fields, escaped quotes, and
// commas/newlines inside quotes. Good enough for exported audience lists.
const parseCsv = (text) => {
  const s = String(text).replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  let i = 0;
  while (i < s.length) {
    const c = s[i];
    if (inQuotes) {
      if (c === '"') {
        if (s[i + 1] === '"') { field += '"'; i += 2; continue; }
        inQuotes = false; i += 1; continue;
      }
      field += c; i += 1; continue;
    }
    if (c === '"') { inQuotes = true; i += 1; continue; }
    if (c === ',') { row.push(field); field = ''; i += 1; continue; }
    if (c === '\n') { row.push(field); rows.push(row); row = []; field = ''; i += 1; continue; }
    field += c; i += 1;
  }
  if (field.length > 0 || row.length > 0) { row.push(field); rows.push(row); }
  return rows.filter((r) => r.some((v) => String(v).trim() !== ''));
};

const norm = (v) => String(v).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

const ALIASES = {
  email: ['email', 'e_mail', 'email_address', 'mail'],
  first_name: ['first', 'first_name', 'firstname', 'fname', 'given_name'],
  last_name: ['last', 'last_name', 'lastname', 'lname', 'surname', 'family_name'],
  company: ['company', 'company_name', 'organization', 'organisation', 'account', 'employer'],
  role: ['role', 'title', 'job_title', 'position'],
  plan_tier: ['plan', 'plan_tier', 'tier', 'plan_name'],
  segment: ['segment', 'stage', 'lifecycle', 'type'],
};

const guessKey = (header, targets) => {
  const h = norm(header);
  const exact = targets.find((t) => norm(t.key) === h || norm(t.label) === h);
  if (exact) return exact.key;
  const aliasHit = Object.entries(ALIASES).find(([, arr]) => arr.includes(h));
  if (aliasHit && targets.some((t) => t.key === aliasHit[0])) return aliasHit[0];
  return IGNORE;
};

const ImportCsvModal = ({ materials = [], materialType = 'walkthrough', onClose, onDone }) => {
  const kindWord = materialType === 'paths' ? 'path' : 'walkthrough';

  const [attrCatalog, setAttrCatalog] = useState({ standard: [], custom: [] });
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [dataRows, setDataRows] = useState([]);
  const [mapping, setMapping] = useState([]); // parallel to headers: attribute key or IGNORE
  const [grantAccess, setGrantAccess] = useState(false);
  const [grantMaterialId, setGrantMaterialId] = useState(materials[0]?.id || null);
  const [sendBranded, setSendBranded] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    http_get(API_ENDPOINTS.GET_PERSONALIZATION_ATTRIBUTES)
      .then(({ data }) => { if (!cancelled) setAttrCatalog({ standard: data.standard || [], custom: data.custom || [] }); })
      .catch(() => { if (!cancelled) setAttrCatalog({ standard: [], custom: [] }); });
    return () => { cancelled = true; };
  }, []);

  // Mapping targets: email (required) + every catalog attribute (standard incl.
  // first/last name — CSV is per-person — plus company-defined custom).
  const targets = useMemo(() => {
    const std = attrCatalog.standard || [];
    const custom = attrCatalog.custom || [];
    const base = std.some((t) => t.key === 'email') ? [] : [{ key: 'email', label: 'Email' }];
    return [...base, ...std, ...custom];
  }, [attrCatalog]);

  const onFile = (file) => {
    if (!file) return;
    setError('');
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      const rows = parseCsv(reader.result);
      if (rows.length === 0) { setError('That file looks empty.'); setHeaders([]); setDataRows([]); return; }
      const hdr = rows[0].map((h) => String(h).trim());
      setHeaders(hdr);
      setDataRows(rows.slice(1));
      setMapping(hdr.map((h) => guessKey(h, targets.length ? targets : [{ key: 'email', label: 'Email' }])));
    };
    reader.onerror = () => setError('Could not read that file.');
    reader.readAsText(file);
  };

  // Re-run auto-mapping once the catalog arrives after a file was already chosen.
  useEffect(() => {
    if (headers.length && targets.length && mapping.every((m) => m === IGNORE)) {
      setMapping(headers.map((h) => guessKey(h, targets)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targets]);

  const setColMap = (colIndex, key) => setMapping((prev) => prev.map((m, i) => (i === colIndex ? key : m)));

  const emailMapped = mapping.includes('email');

  const builtRows = useMemo(() => {
    if (!emailMapped) return [];
    const out = [];
    dataRows.forEach((cells) => {
      const obj = {};
      headers.forEach((_, ci) => {
        const key = mapping[ci];
        if (!key) return;
        const val = String(cells[ci] ?? '').trim();
        if (val) obj[key] = val;
      });
      const email = String(obj.email || '').trim();
      if (email && email.includes('@')) out.push(obj);
    });
    return out;
  }, [dataRows, headers, mapping, emailMapped]);

  const importable = builtRows.length;
  const skippable = dataRows.length - importable;

  const reset = () => {
    setFileName(''); setHeaders([]); setDataRows([]); setMapping([]); setError('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const submit = async () => {
    if (!importable) return;
    if (grantAccess && !grantMaterialId) { setError('Pick a material to grant access to, or turn that option off.'); return; }
    setSubmitting(true);
    setError('');
    try {
      const { data, status } = await http_post(API_ENDPOINTS.POST_AUDIENCE_IMPORTS, {
        rows: builtRows,
        grant_access: grantAccess,
        learning_material_id: grantAccess ? grantMaterialId : null,
        send_branded_email: sendBranded,
      });
      if (data?.error || status >= 300) { setError(data.error || 'Import failed.'); return; }
      const created = data.created || 0;
      const updated = data.updated || 0;
      const skipped = (data.skipped || []).length;
      toast.success(`Imported ${created} new · updated ${updated}`);
      if (skipped) toast.info(`${skipped} row${skipped === 1 ? '' : 's'} skipped (missing/invalid email)`);
      if (data.invited) {
        const inv = (data.invited.created || []).length;
        if (inv) toast.success(`Granted access to ${inv} ${inv === 1 ? 'person' : 'people'}`);
      }
      onDone?.();
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const hasFile = headers.length > 0;
  const previewRows = builtRows.slice(0, 5);
  const previewCols = useMemo(() => targets.filter((t) => mapping.includes(t.key)), [targets, mapping]);

  return (
    <div className="ma-invite-overlay" onClick={onClose}>
      <div className="ma-invite ma-import" onClick={(e) => e.stopPropagation()}>
        <div className="im-h">
          <div className="kk">Import audience</div>
          <h4>Import people from a CSV</h4>
          <p>Map your columns, then import. Existing people keep the values they already have — only blank fields get filled.</p>
          <span className="x" onClick={onClose}>✕</span>
        </div>

        <div className="im-b">
          {!hasFile ? (
            <div className="csv-drop" onClick={() => fileRef.current?.click()}>
              <div className="csv-drop__icon">⬆</div>
              <div className="csv-drop__t">Choose a CSV file</div>
              <div className="csv-drop__d">First row should be column headers. One person per row, with an email column.</div>
              <button type="button" className="btn btn-default btn-sm">Browse…</button>
            </div>
          ) : (
            <>
              <div className="csv-file">
                <span className="csv-file__nm">▤ {fileName}</span>
                <span className="csv-file__meta">{dataRows.length} row{dataRows.length === 1 ? '' : 's'}</span>
                <button type="button" className="csv-file__x" onClick={reset}>Change file</button>
              </div>

              <p className="flabel" style={{ marginTop: 14 }}>Map columns</p>
              <div className="csv-map">
                {headers.map((h, ci) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div className="csv-map__row" key={`${h}-${ci}`}>
                    <span className="csv-map__col" title={h}>{h || <em>(unnamed)</em>}</span>
                    <span className="csv-map__arrow">→</span>
                    <select
                      className="csv-map__sel"
                      value={mapping[ci] || IGNORE}
                      onChange={(e) => setColMap(ci, e.target.value)}
                    >
                      <option value={IGNORE}>— Ignore —</option>
                      {targets.map((t) => (
                        <option key={t.key} value={t.key}>{t.label}{t.key === 'email' ? ' (required)' : ''}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>

              {!emailMapped && (
                <div className="csv-warn">Map one column to <strong>Email</strong> to continue — it’s how people are matched and created.</div>
              )}

              {emailMapped && previewRows.length > 0 && (
                <>
                  <p className="flabel" style={{ marginTop: 14 }}>Preview · first {previewRows.length} of {importable}</p>
                  <div className="csv-preview">
                    <table>
                      <thead>
                        <tr>{previewCols.map((c) => <th key={c.key}>{c.label}</th>)}</tr>
                      </thead>
                      <tbody>
                        {previewRows.map((r, ri) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <tr key={ri}>{previewCols.map((c) => <td key={c.key}>{r[c.key] || <span className="csv-preview__empty">—</span>}</td>)}</tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              <div className="optbox" style={{ marginTop: 16 }}>
                <div className="oh">Access (optional)</div>
                <div className="opt" onClick={() => setGrantAccess((v) => !v)}>
                  <span className={`cb ${grantAccess ? 'on' : ''}`}>{grantAccess ? '✓' : ''}</span>
                  <div>
                    <div className="ot">Also grant access to a {kindWord}</div>
                    <div className="od">Give everyone in this import access to one {kindWord}. Off by default.</div>
                  </div>
                </div>
                {grantAccess && (
                  <div className="csv-grant">
                    <select
                      className="csv-grant__sel"
                      value={grantMaterialId || ''}
                      onChange={(e) => setGrantMaterialId(Number(e.target.value))}
                    >
                      <option value="" disabled>Choose a {kindWord}…</option>
                      {materials.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </select>
                    <div className="opt" style={{ marginTop: 10 }} onClick={() => setSendBranded((v) => !v)}>
                      <span className={`cb ${sendBranded ? 'on' : ''}`}>{sendBranded ? '✓' : ''}</span>
                      <div>
                        <div className="ot">Send branded invite email</div>
                        <div className="od">Emails a magic link to each person. Leave off to grant access silently.</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {error && <div className="im-err">{error}</div>}
        </div>

        <div className="im-f">
          <span className="cnt">
            {hasFile ? `${importable} to import${skippable > 0 ? ` · ${skippable} skipped` : ''}` : 'No file selected'}
          </span>
          <button className="btn btn-primary btn-sm" disabled={!importable || submitting} onClick={submit} type="button">
            {submitting ? 'Importing…' : `Import ${importable || ''} ${importable === 1 ? 'person' : 'people'}`.trim()}
          </button>
        </div>

        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          style={{ display: 'none' }}
          onChange={(e) => onFile(e.target.files?.[0])}
        />
      </div>
    </div>
  );
};

export default ImportCsvModal;
